// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import colors from "@bg/XDL/PreStyle/colors";
import { xBaseTheme } from "@bg/XDL/types";
import { xScreen } from "@bg/XDL/Utilities";
import { styledScrollbar } from "@bg/XDL/Utilities/sharedStyles";
import ModalPresentationPropsDefault from "@insite/mobius/Modal/presentationProps";
import { get } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

export const modalSizeVariants = {
    small: 40,
    medium: 60,
    large: 95,
};

const textColor = get(colors, "text.main");
const closeIconColor = get(colors, "common.ui1");
const primaryMainColor = get(colors, "primary.main");
const backgroundColor = get(colors, "common.ui3");

export default {
    defaultProps: {
        ...ModalPresentationPropsDefault,
        closeButtonProps: {
            ...ModalPresentationPropsDefault?.closeButtonProps,
            css: css`
                position: absolute;
                top: 12px;
                right: 12px;
                padding: 0 0;
                background: transparent !important;
                border: 0 !important;
                box-shadow: none !important;
                svg {
                    width: 18px;
                    height: 18px;
                    color: ${getColor("common.ui1")};
                    fill: ${getColor("common.ui1")};
                }

                &,
                span {
                    color: ${closeIconColor};
                }
            `,
        },
        cssOverrides: {
            modalContainer: css`
                &&&.add-top-padding #modalTitle {
                    padding-top: 67px;
                    padding-bottom: 20px;
                }
            `,
            modalBody: css`
                position: relative;
                max-width: ${modalSizeVariants.small}%;
            `,
            modalTitle: css`
                padding: 45px 40px 30px 45px;
                ${xScreen("lte", "sm")} {
                    padding: 20px 20px 28px 20px;
                }

                color: ${textColor};
                background-color: ${getColor("common.white")};
                border: 0;
            `,
            modalContent: css`
                padding: 0 40px 45px 45px;
                ${xScreen("lte", "sm")} {
                    padding: 0 20px 28px 20px;
                }
                color: ${textColor};
                background-color: ${getColor("common.white")};
                ${styledScrollbar()}
                .modal-actions {
                    // buttons cut-off on shadow fix
                    padding: 50px 0 5px 0px;
                    background-color: ${getColor("common.white")};
                    display: flex;
                    margin: 0;
                    margin-top: 0;
                    justify-content: flex-start;
                    width: 100%;

                    button {
                        margin-left: 0;
                        margin-right: 20px;
                    }

                    button + button {
                        margin-right: 0;
                        margin-left: 0;
                    }

                    ${xScreen("lte", "xs")} {
                        justify-content: center;
                        text-align: center;
                        flex-wrap: wrap;
                        padding-left: 0;
                        padding-right: 0;
                        margin-left: 0;
                        margin-right: 0;

                        button {
                            margin-right: 0;
                            margin-left: 0;
                            margin-bottom: 10px;
                            width: 100%;
                        }
                    }
                }
            `,
            headlineTypography: css`
                margin-bottom: 0;
                @media only screen and (max-width: 426px) {
                    padding-top: 30px;
                }
            `,
        },
        headlineTypographyProps: {
            variant: "h3",
            css: css`
                margin-bottom: 0;
            `,
        },
        closeButtonIconProps: {
            ...ModalPresentationPropsDefault?.closeButtonIconProps,
            color: "secondary",
        },
    },
} as xBaseTheme["modal"];
